import { Box, Flex, HStack, useDisclosure } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { AppLink, CtaButton, RichText } from "@/components";
import { BRAND_LABEL, HOME_PATH, NAV_ACTIONS, NAV_ITEMS, SCROLL_SPY_IDS, media, resolveAnchor } from "@/content";
import { useScrollSpy, useStickyNav } from "@/hooks";
import { NavDrawer } from "./NavDrawer";

const NAV_BAR_BG = "rgba(245, 243, 240, 0.72)";

const NAV_BAR_BG_STUCK = "rgba(245, 243, 240, 0.88)";

export const Nav = () => {
  const { pathname } = useLocation();
  const isHome = pathname === HOME_PATH;
  const { isStuck, sentinelRef } = useStickyNav();
  const activeId = useScrollSpy(SCROLL_SPY_IDS, isHome);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const items = NAV_ITEMS.map((item) => ({ ...item, href: resolveAnchor(item.href, isHome) }));

  const currentId = isHome ? activeId : (NAV_ITEMS.find((item) => item.href === pathname)?.id ?? null);

  return (
    <>
      <Box ref={sentinelRef} position="absolute" top={0} h="1px" w="1px" />

      <Box as="header" className="print-hide" position="sticky" top={0} zIndex={100} px="pad" py="12px">
        <Flex
          maxW="maxw"
          mx="auto"
          align="center"
          gap="clamp(16px, 3vw, 40px)"
          pl="20px"
          pr="12px"
          py="10px"
          borderRadius="md"
          bg={isStuck ? NAV_BAR_BG_STUCK : NAV_BAR_BG}
          backdropFilter="blur(16px) saturate(1.4)"
          border="1px solid"
          borderColor="line.hairline"
          boxShadow={isStuck ? "0 8px 30px -18px rgba(0, 0, 0, 0.5)" : "none"}
          transitionProperty="background-color, box-shadow"
          transitionDuration="0.3s"
          transitionTimingFunction="brand"
        >
          <AppLink href={isHome ? "#top" : HOME_PATH} aria-label={BRAND_LABEL} display="inline-block" flexShrink={0}>
            <img
              src={media("variant3d-logo.png")}
              alt="VARIANT3D"
              width={447}
              height={76}
              style={{ height: 18, width: "auto", filter: "invert(1)" }}
            />
          </AppLink>

          <HStack
            as="nav"
            aria-label="Primary"
            display={{ base: "none", lg: "flex" }}
            ms="auto"
            spacing="clamp(18px, 2.4vw, 34px)"
            fontSize="clamp(0.9375rem, 1.05vw, 1.125rem)"
            fontWeight="light"
          >
            {items.map((item) => (
              <AppLink
                key={item.id}
                href={item.href}
                variant="nav"
                aria-current={currentId === item.id ? (item.kind === "route" ? "page" : "true") : undefined}
              >
                <RichText nodes={item.label} />
              </AppLink>
            ))}
          </HStack>

          <HStack spacing="12px" display={{ base: "none", lg: "flex" }}>
            {NAV_ACTIONS.map((action) => (
              <CtaButton key={action.label} href={action.href} variant={action.variant} size="nav">
                {action.label}
              </CtaButton>
            ))}
          </HStack>

          <Box
            as="button"
            type="button"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            onClick={onOpen}
            display={{ base: "grid", lg: "none" }}
            ms="auto"
            w="46px"
            h="46px"
            placeContent="center"
            gap="6px"
            borderRadius="sm"
          >
            <Box w="20px" h="1.5px" bg="ink.DEFAULT" />
            <Box w="20px" h="1.5px" bg="ink.DEFAULT" />
          </Box>
        </Flex>
      </Box>

      <NavDrawer isOpen={isOpen} onClose={onClose} items={items} activeId={currentId} />
    </>
  );
};
